"use client";

import { useUmamiAnalytics } from "@analytics";
import { LocaleLink } from "@i18n/routing";
import { IMAGE_HERO_EXAMPLE_EVENT } from "@marketing/home/components/HeroControls";
import { scrollToGenerator } from "@marketing/home/components/ScrollToGeneratorButton";
import { cn } from "@ui/lib";
import { ChevronDownIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import type { ImageHeroExample } from "./ImageGeneratorHero";

/**
 * 两列（< lg）时先只显示前两个子块，下面出「Show more」，点了全部展开；lg 起不折叠、不出按钮。
 * 收起的块只是 display:none，内容照样在 HTML 里（样片墙的 prompt 原文要给谷歌看），里面 lazy 的图展开前不加载
 */
export function MobileCollapse({
	label,
	className,
	children,
}: {
	label: string;
	className?: string;
	children: ReactNode;
}) {
	const [expanded, setExpanded] = useState(false);
	return (
		<>
			<div
				className={cn(
					className,
					!expanded && "max-lg:[&>*:nth-child(n+3)]:hidden",
				)}
			>
				{children}
			</div>
			{expanded ? null : (
				<button
					type="button"
					onClick={() => setExpanded(true)}
					className="mx-auto mt-5 flex h-11 cursor-pointer items-center gap-1.5 rounded-full border border-gray-4 bg-white px-6 font-medium text-gray-12 text-sm lg:hidden"
				>
					{label}
					<ChevronDownIcon className="size-4" />
				</button>
			)}
		</>
	);
}

/** 样片区 / Prompt 样片墙的「Try this prompt」：把 prompt（和参考图）填进 hero，再滚回去 */
export function UseImagePromptButton({
	id,
	placement,
	example,
	scrollTargetId,
	className,
	children,
}: {
	id: string;
	/** 记进 Umami，区分左右交替的样片区、样片墙和 Magic 对比 */
	placement: "examples" | "gallery" | "magic";
	example: ImageHeroExample;
	/** 没有生图 hero 的页面（模板页）滚回这个 id 的工作台；不传滚回生图 hero */
	scrollTargetId?: string;
	className?: string;
	children: ReactNode;
}) {
	const { trackEvent } = useUmamiAnalytics();
	return (
		<button
			type="button"
			onClick={() => {
				window.dispatchEvent(
					new CustomEvent(IMAGE_HERO_EXAMPLE_EVENT, {
						detail: example,
					}),
				);
				if (scrollTargetId) {
					document
						.getElementById(scrollTargetId)
						?.scrollIntoView({
							behavior: "smooth",
							block: "start",
						});
				} else {
					scrollToGenerator();
				}
				void trackEvent("image-example-clicked", { id, placement });
			}}
			className={className}
		>
			{children}
		</button>
	);
}

/** 页尾互推块的按钮：图片页到视频页的唯一入口，单独记点击看有没有人走这条路 */
export function ImageToVideoPromoLink({
	className,
	children,
}: {
	className?: string;
	children: ReactNode;
}) {
	const { trackEvent } = useUmamiAnalytics();
	return (
		<LocaleLink
			href="/image-to-video"
			onClick={() =>
				void trackEvent("image-to-video-promo-clicked", {
					from: "/ai-image-generator",
				})
			}
			className={className}
		>
			{children}
		</LocaleLink>
	);
}
